import { ClassKey } from '@const/class.constants';
import { MenuResponseDTO } from '@dto/menuResponse.dto';
import { DashboardContextMenu } from '@parts/dashboard/select/DashboardContextMenu';
import { Button, GetRef, Tooltip, Tree } from 'antd';
import { DataNode, EventDataNode } from 'antd/es/tree';
import { AxiosError } from 'axios';
import React, { Key, useEffect, useMemo, useRef, useState } from 'react';
import { FaMinus, FaRegStar } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router';
import { useAppGlobStore } from 'src/appGlobalStore';
import { findLastTopologyNode } from 'src/boxParts/topology/topologyPrefs';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import { FolderContextMenuBase, MenuDataNode as FolderMenuDataNode } from 'src/content/configuration/FolderContextMenuBase';
import { ContextState, CustomFolderContextMenu } from 'src/content/configuration/cg/CustomFolderContextMenu';
import HistorySearch from 'src/content/search/components/SearchHistory';
import { API_URL } from 'src/data/Api';
import { Const } from 'src/data/Const';
import { useWebSocketReloadGetRandomNumberOnReload } from 'src/hook/useWebSocketReload';
import { MenuNode } from 'src/model/MenuNode';
import { mergeTree } from 'src/routing/NavigateRoute';
import { getPathUrl } from 'src/routing/pathUrl';
import { DataService } from 'src/service/data.service';
import { MenuService } from 'src/service/menu.service';
import { getApi } from 'src/util/apiCalls';
import { reloadPage, setReloadNavigate } from 'src/util/reloadPage';
import { RoutePath, isDevicesTree } from '../../data/Routes';
import { Log } from '../../service/log.service';
import { GLOB } from '../../util/Glob';
import { GroupNavFolderContextMenu } from './GroupNavFolderContextMenu';
import LeftMenuSearch from './Search/LeftMenuSearch';
import './SideNavBar.less';

const { DirectoryTree } = Tree;

const TREE_HEIGHT_FALLBACK = 400;
const ITEM_HEIGHT_FALLBACK = 17;
/** Share of the tree body the search results keep even when the history block is tall. */
const SEARCH_RESULTS_MIN_SHARE = 0.6;
const EMPTY_NODES: MenuNode[] = [];
const SCROLL_REQUEST_TTL = 5000;
/**
 * The events rc-virtual-list cancels to take scrolling over from the browser. `wheel` is
 * the one it cancels everywhere except Firefox, where `MozMousePixelScroll` does it
 * instead; `touchmove` is the touch equivalent, which it attaches to the touched row
 * rather than to the holder. All of them have to be kept away from it for the browser to
 * scroll. (`DOMMouseScroll` and `touchstart`/`touchend` are passive and cannot cancel
 * anything, so they can keep firing harmlessly.)
 */
const RC_SCROLL_EVENTS = ['wheel', 'MozMousePixelScroll', 'touchmove'] as const;

/** Sections with a self-managed left menu — excluded from the server-tree reload/refresh effects. */
const ROUTES_WITH_STATIC_LEFT_MENU: readonly RoutePath[] = [
  RoutePath.DASHBOARD, RoutePath.SETTINGS, RoutePath.PROFILE,
  RoutePath.ALERTING, RoutePath.EXPORTER, RoutePath.REPORTER, RoutePath.EVENTS,
];

function getGroupParent(node: MenuNode) {
  return MenuNode.getTopParent(node, (node) => !node?.group_id && !node?.isLeaf && (node.isStorageGroup || node.isGroupFolder));
}

/** Root of the last `spreadTree` copy; consumers store it back so the tree gets a new identity. */
let newTree: MenuNode;
/** Shallow-copies the path from the root down to `node` and returns the copy of `node`. */
function spreadTree(node: MenuNode, tree: MenuNode): MenuNode {
  if (!node.parent) {
    return (newTree = { ...tree });
  }
  const parent = spreadTree(node.parent, tree);
  let found: MenuNode = parent;
  if (parent?.children)
    parent.children = parent.children.map(n => n.key === node.key ? (found = { ...n }) : n);
  return found;
}

/** Snapshot of the current selection; the returned check tells an async menu response it arrived after the user navigated away. */
function captureSelection() {
  const { selectedClass: cls, selectedAllType: aType, selectedType: type } = GLOB;
  return () => cls !== GLOB.selectedClass || aType !== GLOB.selectedAllType || (type !== GLOB.selectedType && !aType);
}

function currentItemSearch() {
  const { pathname, search } = GLOB.location;
  return pathname.substring(pathname.lastIndexOf('/')) + search;
}

function hasStaticLeftMenu() {
  return ROUTES_WITH_STATIC_LEFT_MENU.includes(GLOB.selectedClass);
}

/**
 * Height the element's content wants, independent of how much room it actually got.
 * Its own scrollHeight is useless once it clips its children (they scroll instead), so
 * this sums the children: scrollHeight covers a child that scrolls internally, and the
 * bounding box covers one that does not. Margins are added because scrollHeight omits
 * them. Used to reserve room for the search-history block without letting its squeezed
 * layout feed back into the height it is granted.
 */
function measureNaturalHeight(element: HTMLElement | undefined): number {
  if (!element) return 0;
  let total = 0;
  for (const child of Array.from(element.children)) {
    const style = getComputedStyle(child);
    total += Math.max(child.scrollHeight, child.getBoundingClientRect().height)
      + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
  }
  return Math.ceil(total);
}

function makeLinkPath(node: MenuNode): string[] {
  if (!node?.parent)
    return [node.path];

  return [...makeLinkPath(node.parent), node.path];
}

let prevKey = '';
let selectedFromTree = false;

export const SideNavBar: React.FC = () => {
  const navigate = useNavigate();
  setReloadNavigate(navigate);
  GLOB.location = useLocation();
  const leftMenuSelectedKeys = useAppGlobStore(s => s.leftMenuSelectedKeys);
  const topMenu = useAppGlobStore(s => s.topMenu);
  const leftMenu = useAppGlobStore(s => s.leftMenu);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [loadedKeys, setLoadedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const stateRef = useRef({ groupReload: false, timeout: 0, selectFirst: false, reloading: false, leftMenu });
  const state = stateRef.current;
  const treeRef = useRef<GetRef<typeof DirectoryTree>>(undefined);
  const domRef = useRef<HTMLElement>(undefined);
  const treeBoxRef = useRef<HTMLDivElement>(undefined);
  const historyRef = useRef<HTMLDivElement>(undefined);
  const aborter = useRef<AbortController>(undefined);
  const [treeHeight, setTreeHeight] = useState(TREE_HEIGHT_FALLBACK);
  const [itemHeight, setItemHeight] = useState(ITEM_HEIGHT_FALLBACK);
  /** Mirror of treeHeight readable from a rAF callback without re-arming effects. */
  const heightRef = useRef(TREE_HEIGHT_FALLBACK);
  const pendingScroll = useRef<{ key: Key; until: number; }>(undefined);
  const dashContextMenu = useMemo(() => new DashboardContextMenu(
    () => ({ options: leftMenu.children }),
    ({ options }) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: options } }),
    item => item.isLeaf, null,
    deletedNode => deletedNode.key === GLOB.selectedItem.key && navigate(GLOB.getDefaultPath()),
    reloadPage, false
  ),
    [leftMenu]);
  const cgContextMenu = useMemo(() => new CustomFolderContextMenu(() => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state: ContextState) => useAppGlobStore.setState({ leftMenu: ({ ...leftMenu, children: [...state.folders[0].children] }) }), null, true), [leftMenu]);
  const reloadFolderTree = (serverRefetch?: boolean) => {
    if (!serverRefetch) return;
    if (GLOB.selectedClass === RoutePath.STORAGE) {
      void GLOB.menuService.reloadStorageGroups().then(() => {
        const newChildren = GLOB.menuService.storagesBy.group.children;
        state.leftMenu.children = newChildren;
        MenuNode.mapParent(newChildren, state.leftMenu);
        setExpandedKeys([]);
        setLoadedKeys([]);
        useAppGlobStore.setState({ leftMenu: { ...state.leftMenu, children: [...newChildren] } });
      });
    } else {
      reloadTree();
    }
  };
  const sgFolderContextMenu = useMemo(() => new GroupNavFolderContextMenu(
    () => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...state.folders[0].children] } }),
    null, { class: 'storage' }, reloadFolderTree
  ), [leftMenu]);
  const serverFolderContextMenu = useMemo(() => new GroupNavFolderContextMenu(
    () => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...state.folders[0].children] } }),
    null, {}, reloadFolderTree
  ), [leftMenu]);
  const wsReload = useWebSocketReloadGetRandomNumberOnReload('menu');

  const treeTitle = getTreeTitle();
  const isSearch = treeTitle === "Search";
  const searchHistoryLimit = GLOB?.userInfo?.configuration?.searchHistoryLimit;
  const showHistory = isSearch && !!searchHistoryLimit;

  useEffect(() => {
    return () => {
      window.clearTimeout(state.timeout);
    };
  });

  /**
   * Feeds the `height` prop that switches rc-virtual-list into virtual mode.
   * `.left-tree-body` is sized purely by its parent (flex-basis 0 + min-height 0),
   * so its height can never be derived from the tree content - no measure/layout loop.
   */
  useEffect(() => {
    const box = treeBoxRef.current;
    if (!box) return;
    const measure = () => {
      const boxHeight = box.getBoundingClientRect().height;
      const historyHeight = measureNaturalHeight(historyRef.current);
      const height = Math.floor(historyHeight
        ? Math.max(boxHeight - historyHeight, boxHeight * SEARCH_RESULTS_MIN_SHARE)
        : boxHeight);

      if (height <= 0) return;
      heightRef.current = height;
      setTreeHeight(prev => Math.abs(prev - height) < 1 ? prev : height);

      const pitch = parseFloat(getComputedStyle(box).getPropertyValue('--xm-tree-item-height'));
      if (pitch > 0) setItemHeight(prev => prev === pitch ? prev : pitch);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    if (historyRef.current) observer.observe(historyRef.current);
    return () => observer.disconnect();
  }, [showHistory]);

  useEffect(() => {
    if (wsReload) {
      void GLOB.menuService.init();
      if (hasStaticLeftMenu()) return;
      reloadTree();
    }
  }, [wsReload]);

  useEffect(() => {
    aborter.current = new AbortController();
    const interval = setInterval(() => {
      void GLOB.menuService.init();
      if (hasStaticLeftMenu()) return;

      const isStale = captureSelection();
      const getUrl = getPathUrl(GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedGroupId, GLOB.selectedType, currentItemSearch());
      getApi<MenuResponseDTO>(getUrl, aborter.current.signal).then(response => {
        if (isStale()) return;
        const incoming = response.data.data;
        const leftMenu = useAppGlobStore.getState().leftMenu;
        if (leftMenu.children.some(mn => incoming.some(mi => mi.url === mn.url))) {
          mergeTree(leftMenu.children, response.data.data, GLOB.selectedGroupId);
          useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...leftMenu.children] } });
        } else {
          for (const subMenu of leftMenu.children) {
            if (subMenu.children?.some(mn => incoming.some(mi => mi.url === mn.url))) {
              mergeTree(subMenu.children, incoming, GLOB.selectedGroupId);
              useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...leftMenu.children] } });
              return;
            }
          }
          Log.debug(`Failed to update left tree menu ${location.href} with ${getUrl}!`);
        }
      }, (reason: AxiosError) => Log.error('Failed to refresh current tree', reason));
    }, 1000 * 60 * 60);
    return () => {
      clearInterval(interval);
      aborter.current.abort();
    };
  }, []);

  useEffect(() => {
    const box = treeBoxRef.current;
    if (!box) return;
    const swallow = (event: Event) => {
      if ((event.target as HTMLElement)?.closest?.('.ant-tree-list-holder')) event.stopPropagation();
    };
    for (const name of RC_SCROLL_EVENTS) box.addEventListener(name, swallow, { capture: true });
    return () => {
      for (const name of RC_SCROLL_EVENTS) box.removeEventListener(name, swallow, { capture: true });
    };
  }, []);

  useEffect(() => {
    GLOB.scrollSelectedItemIntoView = () => {
      const item = GLOB.selectedItem;
      if (!item) return;
      pendingScroll.current = { key: item.key, until: Date.now() + SCROLL_REQUEST_TTL };
      setExpandedKeys(prev => [...prev, ...MenuNode.getParentKeys(item.isLeaf ? item.parent : item)].filter(GLOB.uniqueArrayFilter));
    };
  }, []);

  useEffect(() => {
    const pending = pendingScroll.current;
    if (!pending) return;
    if (Date.now() > pending.until) {
      pendingScroll.current = undefined;
      return;
    }

    const item = GLOB.selectedItem;
    if (item?.key !== pending.key || !isWithinTree(item)) return;
    if (!MenuNode.getParentKeys(item.parent).every(k => expandedKeys.includes(k))) return;

    const raf = requestAnimationFrame(() => {
      pendingScroll.current = undefined;
      treeRef.current?.scrollTo({
        key: item.key,
        align: 'top',
        offset: Math.max(0, Math.round((heightRef.current - itemHeight) / 2))
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [expandedKeys, leftMenu, leftMenuSelectedKeys, treeHeight, itemHeight]);

  useEffect(() => {
    if (selectedFromTree || !leftMenuSelectedKeys?.length) {
      selectedFromTree = false;
    } else
      GLOB.scrollSelectedItemIntoView();
  }, [leftMenuSelectedKeys]);

  useEffect(() => {
    window.clearTimeout(state.timeout);
    state.leftMenu = leftMenu;
    if (!leftMenu) return;

    if (prevKey !== leftMenu.key && !leftMenu.isSearchTree) {
      setLoadedKeys([]);
      setExpandedKeys([]);
    }

    if (leftMenu.url && !leftMenu.children) {
      GLOB.treeLoaded.reset();
      const isStale = captureSelection();
      getApi<MenuResponseDTO>(leftMenu.url, aborter.current.signal).then(
        (response) => {
          if (isStale()) return;
          useAppGlobStore.setState((prev) => {
            if (prev.leftMenu) spreadTree(prev.leftMenu, prev.leftMenu);
            else newTree = leftMenu;
            newTree.children = response.data.data.map((mi) => MenuNode.menuItemToNode(mi));
            state.leftMenu = { ...newTree };
            return { leftMenu: newTree };
          });
          if (!GLOB.selectedItem) {
            setTimeout(() => {
              navigate(GLOB.selectedClass + GLOB.selectedType + GLOB.getItemPath(newTree.children[0]), { replace: true });
            });
          }
          GLOB.treeLoaded.resolve();
        },
        (reason: AxiosError) => {
          Log.error('Failed to get tree!', reason);
          GLOB.treeLoaded.reject();
        }
      );
    } else {
      GLOB.treeLoaded.resolve();
    }
    GLOB.treeLoaded.promise.then(() => {
      const leftMenu = state.leftMenu;
      MenuNode.mapParent(leftMenu?.children, leftMenu);

      if (GLOB.selectedItem)
        if (prevKey !== leftMenu?.key) {
          if (!GLOB.selectedItem.isLeaf && !GLOB.selectedItem.children)
            setLoadedKeys(expandedKeys.filter(k => k !== GLOB.selectedItem.key));
          else
            setLoadedKeys([...expandedKeys]);
          GLOB.scrollSelectedItemIntoView();
        } else if (!treeRef.current.props.selectedKeys.some(k => k === GLOB.selectedItem.key)) {
          GLOB.scrollSelectedItemIntoView();
        }

      prevKey = leftMenu?.key as string;

    }, (reason: AxiosError) => console.error('Tree not loaded', reason));
  }, [leftMenu]);

  function isWithinTree(node: MenuNode) {
    if (node === leftMenu || node?.key === leftMenu?.key) return true;
    if (node.parent) return isWithinTree(node.parent);
    return false;
  }

  function reloadLeftMenu() {
    state.reloading = true;
    GLOB.selectedAllType = GLOB.selectedClass = GLOB.selectedGroupId = GLOB.selectedType = GLOB.selectedItem = null;
    setLoadedKeys([]);
    setExpandedKeys([]);
    MenuNode.mapParent(leftMenu?.children, leftMenu);
    setTimeout(() => {
      reloadPage();
      state.reloading = false;
    });
  }
  DataService.reloadLeftMenu = reloadLeftMenu;

  function reloadTree() {
    const isStorage = GLOB.selectedClass === RoutePath.STORAGE;
    const folderPath = isStorage ? '/folder/class' + GLOB.selectedClass : '/folder?class=' + GLOB.selectedClass.slice(1);
    const url = GLOB.selectedType ? getPathUrl(GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedGroupId, GLOB.selectedType, currentItemSearch())
      : API_URL.MENU + folderPath;
    const isStale = captureSelection();
    getApi<MenuResponseDTO>(url, aborter.current.signal).then(response => {
      if (isStale()) return;
      if (state.leftMenu.children.some(mn => response.data.data.some(mi => mi.url === mn.url))) {
        state.leftMenu.children = response.data.data.map(mi => MenuNode.menuItemToNode(mi, GLOB.selectedGroupId));
        setExpandedKeys([]);
        setLoadedKeys([]);
        useAppGlobStore.setState(({ leftMenu: { ...state.leftMenu, children: [...state.leftMenu.children] } }));
      } else {
        const keysToStay = state.leftMenu.children.map(sm => sm.url);
        setLoadedKeys(prev => prev.filter(k => keysToStay.some(ks => k === ks)));
        setExpandedKeys(prev => prev.filter(k => keysToStay.some(ks => k === ks)));
        for (const subMenu of state.leftMenu.children) {
          if (subMenu.children?.some(mn => response.data.data.some(mi => mi.url === mn.url))) {
            subMenu.children = response.data.data.map(mi => MenuNode.menuItemToNode(mi, GLOB.selectedGroupId));
            break;
          }
        }
        useAppGlobStore.setState(({ leftMenu: { ...state.leftMenu, children: [...state.leftMenu.children] } }));
      }
      state.groupReload = true;
    }, (reason: AxiosError) => Log.error('Failed to refresh current tree', reason));
  }

  function onLoadData(node: MenuNode) {
    if (state.reloading) return Promise.resolve();
    if (state.groupReload) {
      state.groupReload = false;
      return Promise.resolve();
    }
    if (!isWithinTree(node)) {
      window.clearTimeout(state.timeout);
      state.timeout = window.setTimeout(() => {
        Log.warn(`Node ${node.key} not part of tree ${leftMenu?.key}!`);
      }, Const.TIMEOUT_DEBOUNCE_SHORT);
      setExpandedKeys(prev => prev.filter(k => k !== node.key));
      return Promise.reject(new Error(`Node ${node.key} is not part of the tree ${leftMenu?.key}!`));
    }
    return new Promise<void>((resolve, reject) => {
      if (node.children && (node.children.length || !node.url)) {
        spreadTree(leftMenu, leftMenu);
        useAppGlobStore.setState({ leftMenu: newTree });
        setLoadedKeys(prev => [...prev, node.key as string]);
        setExpandedKeys(prev => [...prev, node.key as string]);
        resolve();
        return;
      }
      GLOB.treeLoaded.reset();
      if (node.url) {
        const isStale = captureSelection();
        getApi<MenuResponseDTO>(node.url, aborter.current.signal).then(
          (response) => {
            if (isStale()) return;
            useAppGlobStore.setState((prev) => {
              const expanded = spreadTree(node, prev.leftMenu);
              const group = GLOB.selectedAllType === RoutePath.GROUP ? MenuNode.getGroupPathSegment(getGroupParent(node)) : '';
              expanded.children = response.data.data.map((mi) => MenuNode.menuItemToNode(mi, group));
              if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS)
                expanded.children.sort(MenuService.getFolderSorter(true));
              else if (GLOB.selectedClass === RoutePath.DASHBOARD)
                expanded.children.sort(MenuService.getFolderSorter(false));
              MenuNode.mapParent(expanded.children, expanded);
              resolve();
              setTimeout(() => {
                selectFirst(expanded.children);
                setLoadedKeys(prev => [...prev, node.key as string]);
                setExpandedKeys(prev => [...prev, node.key as string]);
              });
              const menu = topMenu;
              const root = menu.find(mn => mn.path === GLOB.selectedClass);
              let menuTree = root;
              if (GLOB.selectedType && !GLOB.selectedAllType) {
                menuTree = root.children.find(mn => mn.path === GLOB.selectedType);
              }
              if (menuTree) {
                menuTree.children = newTree.children;
                setTimeout(() => {
                  useAppGlobStore.setState({ topMenu: [...menu] });
                });
              }
              return { leftMenu: newTree };
            });
          },
          (reason: AxiosError) => {
            Log.error('Failed to get sub-menu!', reason);
            GLOB.treeLoaded.reject();
            reject(new Error('Failed to get sub-menu'));
            setLoadedKeys(prev => [...prev, node.key as string]);
          }
        );
      }
    });
  }

  const handleCollapse = () => {
    setExpandedKeys([]);
    treeRef.current?.scrollTo({ top: 0 });
  };

  const onExpand = (
    keys: Key[],
    info: {
      node: EventDataNode<DataNode>;
      expanded: boolean;
      nativeEvent: MouseEvent;
    }
  ) => {
    setExpandedKeys((info.expanded ? keys : keys.filter(k => k !== info.node.key)) as string[]);
  };

  const onSelect = (
    keys: React.Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode<DataNode>;
      selectedNodes: MenuNode[];
      nativeEvent: MouseEvent;
    }
  ) => {
    const selected = info.selectedNodes[0];
    if (!selected) {
      Log.error('Failed to find selected key in left tree!', { keys, info, loc: GLOB.location, leftMenu, });
      return;
    }

    if (selected.isLeaf) {
      useAppGlobStore.setState({ isMobileLeftTreeOpen: false });
    }

    const pathNew = getPathFromMenuNode(selected, topMenu);
    if (pathNew) {
      const pathCurrent = decodeURIComponent(GLOB.location.pathname + GLOB.location.search);
      selectedFromTree = true;
      if (pathNew === pathCurrent) {
        reloadPage();
      } else {
        navigate(pathNew + GLOB.location.hash);
      }
    }
  };

  const titleRender = (node: MenuNode) => {
    const isDash = GLOB.selectedClass === RoutePath.DASHBOARD && !MenuNode.isDashExcluded(node);
    const isCustomGroup = GLOB.selectedClass === RoutePath.CUSTOM_GROUPS;
    const isGroupFolder = !node.isLeaf && !node.isStorageGroup && (
      node.isGroupFolder
      || (GLOB.selectedClass === RoutePath.STORAGE && GLOB.selectedAllType === RoutePath.GROUP)
      || GLOB.selectedClass === RoutePath.SERVER_GROUP
    );

    // context menus render the label themselves and need the plain text kept aside
    if ((isDash || isCustomGroup || isGroupFolder) && !node.titleText && !node.divider)
      node.titleText = node.title as string;

    let title: React.ReactNode;
    if (isDash)
      title = dashContextMenu.createLabel(node);
    else if (isCustomGroup)
      title = cgContextMenu.createLabel(node as FolderMenuDataNode);
    else if (isGroupFolder)
      title = (GLOB.selectedClass === RoutePath.STORAGE ? sgFolderContextMenu : serverFolderContextMenu).createLabel(node as FolderMenuDataNode);
    else
      title = node.titleText ?? node.title;

    if (node.styleClass === 'bold')
      title = <strong>{title}</strong>;
    else if (node.styleClass === 'star')
      title = <div style={{ display: 'inline-flex' }}>{title}<FaRegStar style={{ marginLeft: '0.25em' }} title='Default dashboard' /></div>;

    const handleOpenInNewTab = (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        e.stopPropagation();
        window.open(getPathFromMenuNode(node, topMenu), '_blank');
      }
    };

    return <span onClick={handleOpenInNewTab} onAuxClick={handleOpenInNewTab}>{title}</span>;
  };

  function getTreeTitle() {
    // context menus address the root by this id, so the loaded menu has to carry it
    const rootItem = (root: string) => {
      if (leftMenu) leftMenu.itemId = root;
      return { key: root, itemId: root, titleText: leftMenu?.title as string };
    };

    if (GLOB.selectedClass === RoutePath.DASHBOARD)
      return dashContextMenu.createLabel({ key: DashboardContextMenu.ROOT, itemId: DashboardContextMenu.ROOT, titleText: leftMenu?.title as string });
    if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS)
      return cgContextMenu.createLabel(rootItem(CustomFolderContextMenu.ROOT));
    if (GLOB.selectedClass === RoutePath.STORAGE && GLOB.selectedAllType === RoutePath.GROUP)
      return sgFolderContextMenu.createLabel(rootItem(FolderContextMenuBase.ROOT));
    if (GLOB.selectedClass === RoutePath.SERVER_GROUP)
      return serverFolderContextMenu.createLabel(rootItem(FolderContextMenuBase.ROOT));

    return leftMenu?.titleText ?? leftMenu?.title;
  }

  function selectFirst(children: MenuNode[], expanded?: MenuNode) {
    if (state.selectFirst) {
      state.selectFirst = false;
      const toSelect = expanded?.key === RoutePath.TOPOLOGY ? findLastTopologyNode(children) : MenuNode.selectFirst(children);
      if (toSelect && GLOB.selectedItem !== toSelect)
        onSelect(null, { selectedNodes: [toSelect], event: 'select', nativeEvent: null, node: null, selected: null });
    }
  }

  return (
    <>
      <nav className={`left-tree ${isSearch ? "left-tree-height" : ""}`} ref={domRef}>
        <h3>{treeTitle}</h3>
        {isLoadingSearch &&
          <div className='search-spinner-overlay'>
            <XmSpinner className='search-spinner' />
          </div>
        }
        <div className='left-tree-body' ref={treeBoxRef}>
          <DirectoryTree<MenuNode>
            ref={treeRef}
            blockNode={true}
            defaultExpandParent={true}
            expandedKeys={expandedKeys}
            height={treeHeight}
            itemHeight={itemHeight}
            // no expand animation: while it runs rc-tree hands the virtual list a placeholder
            // row instead of the new nodes, so a scroll to a freshly expanded key cannot find it
            motion={false}
            loadData={onLoadData}
            loadedKeys={loadedKeys}
            onExpand={onExpand}
            onSelect={onSelect}
            selectedKeys={leftMenuSelectedKeys}
            showIcon={false}
            showLine={false}
            titleRender={titleRender}
            className="search-result-info"
            treeData={leftMenu?.children?.length ? leftMenu.children : EMPTY_NODES}
            onClick={(e, node) => {
              if (node.isLeaf || node.expanded) return;
              state.selectFirst = true;
              if (node.children?.length > 0) {
                selectFirst(node.children, node);
              }
            }}
          />
          {showHistory && (
            <div className='left-tree-history' ref={historyRef}>
              <HistorySearch historyState={searchHistoryLimit} userId={GLOB.userInfo.user_id} />
            </div>
          )}
        </div>
      </nav>
      {GLOB.selectedClass && ClassKey[GLOB.selectedClass.slice(1).toUpperCase()] && GLOB.selectedAllType !== RoutePath.GROUP &&
        <div className='search-wrapper' >
          <Tooltip title="Collapse left menu" placement="right">
            <Button className='search-collapse-button' size='small' icon={<FaMinus />} type="primary" onClick={handleCollapse} />
          </Tooltip>
          <LeftMenuSearch onSelect={onSelect} setIsLoading={setIsLoadingSearch} setLoadedKeys={setLoadedKeys} setExtendednKeys={setExpandedKeys} reloadTree={reloadTree} />
        </div>
      }
    </>
  );
};

function getPathFromMenuNode(node: MenuNode, topMenu: MenuNode[]) {
  let pathNew: string[];
  if (node.path && node.isLeaf) {
    pathNew = [GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedType, node.path];
    if (MenuNode.getTopParent(node)?.title === "Logs")
      pathNew = [GLOB.selectedClass, ...makeLinkPath(node)];
  } else if (node.url && node.isLeaf) {
    if (GLOB.selectedType && !([RoutePath.LAN_CISCO, RoutePath.LAN_OTHERS, RoutePath.LAN_FIREWALL_CISCO, RoutePath.LAN_JUNIPER, RoutePath.LAN_FORTIGATE] as string[]).includes(GLOB.selectedType)) {
      const cls = topMenu.find(mn => mn.path === GLOB.selectedClass);
      const qIdx = node.url.indexOf('?');
      const urlNoQ = qIdx >= 0 ? node.url.substring(0, qIdx) : node.url;
      const itemIdx = urlNoQ.lastIndexOf('/');
      const item = node.url.substring(itemIdx);
      const typeIdx = urlNoQ.lastIndexOf('/', itemIdx - 1);
      const typePath = node.url.substring(typeIdx, itemIdx);
      const type = typePath.substring(1);

      if (isDevicesTree(GLOB.selectedAllType)) {
        let groupPath = '';
        if (GLOB.selectedAllType === RoutePath.GROUP) {
          const top = getGroupParent(node);
          if (!top.isLeaf)
            groupPath = '/' + top.itemId;
        }
        const device = MenuNode.getHwTypeParent(node);
        pathNew = [GLOB.selectedClass, GLOB.selectedAllType, groupPath, device ? ('/' + device.titleText) : '', item];
      } else if (cls.children.some(mn => mn.path === typePath || mn.hwType === type)) {
        pathNew = [GLOB.selectedClass, GLOB.selectedAllType, typePath, item];
      } else if (cls.path === typePath) {
        const typeSegment = cls.path === RoutePath.TAPE ? RoutePath.TYPE : GLOB.selectedAllType;
        pathNew = [GLOB.selectedClass, typeSegment, item];
      } else {
        const clsOther = topMenu.find(mn => mn.children.some(c => c.path === typePath));
        if (clsOther) {
          pathNew = [clsOther.path, typePath, item];
        } else {
          //multiple hw_types under fabric
          const nstdClsHwType = topMenu.find(mn => mn?.children?.some(c => c?.children?.some(cc => cc.hwType === type)));
          pathNew = [nstdClsHwType.path, GLOB.selectedAllType, typePath, item];
        }
      }

    } else {
      let groupPath = '';
      if (GLOB.selectedAllType === RoutePath.GROUP) {
        const top = getGroupParent(node);
        if (!top.isLeaf)
          groupPath = '/' + top.itemId;
      }
      const device = MenuNode.getHwTypeParent(node);
      const deviceTypePath = GLOB.selectedClass === RoutePath.CUSTOM_GROUPS || GLOB.selectedClass === RoutePath.DASHBOARD || !device ? '' :
        ('/' + (isDevicesTree(GLOB.selectedAllType) ? device.titleText : device.hwType));
      pathNew = [GLOB.selectedClass, GLOB.selectedAllType, groupPath, deviceTypePath, GLOB.getItemPath(node)];
    }
  }

  return pathNew?.join('');
}